import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { ProductCard } from '@/components/products/ProductCard';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FarmDetail() {
  const { farmId } = useParams();
  const [farm, setFarm] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const fetchFarm = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/farms/${farmId}`);
        setFarm(response.data.farm);
        setProducts(response.data.products);
      } catch (error) {
        console.error('Error fetching farm:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFarm();
  }, [farmId]);

  const handleAddToCart = async (product) => {
    if (!user) {
      toast.error('Please sign in to add items to cart');
      return;
    }
    try {
      await addToCart(product.product_id, 1, 'one-time');
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }

  if (!farm) return null;

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="farm-detail-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img
          src={farm.image_url}
          alt={farm.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-dark/40 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0">
          <div className="container-custom pb-12">
            <Link to="/farms" className="inline-flex items-center gap-2 text-cream/80 font-manrope font-medium mb-4 hover:text-cream transition-colors" data-testid="back-to-farms">
              <ArrowLeft className="w-5 h-5" />
              Back to Farms
            </Link>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-4" data-testid="farm-name">
              {farm.name}
            </h1>
            <div className="flex items-center gap-2 text-cream/80">
              <MapPin className="w-5 h-5" />
              <span className="font-manrope">{farm.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Description */}
            <div className="lg:col-span-2">
              <h2 className="font-playfair text-3xl font-bold text-dark mb-6">About the Farm</h2>
              <p className="font-manrope text-dark/70 text-lg leading-relaxed mb-8" data-testid="farm-description">
                {farm.description}
              </p>

              {/* Practices */}
              <div className="bg-white rounded-2xl p-8 border border-forest/10 mb-8">
                <h3 className="font-playfair text-xl font-bold text-dark mb-4">Farming Practices</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {farm.practices.map((practice, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-forest flex-shrink-0" />
                      <span className="font-manrope text-dark/70">{practice}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div className="bg-white rounded-2xl p-8 border border-forest/10 sticky top-28">
                <h3 className="font-playfair text-xl font-bold text-dark mb-4">Specialties</h3>
                <div className="flex flex-wrap gap-2">
                  {farm.specialties.map((specialty, index) => (
                    <span key={index} className="badge-local">
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products */}
          {products.length > 0 && (
            <div className="mt-16">
              <h2 className="font-playfair text-3xl font-bold text-dark mb-8">Products from {farm.name}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="farm-products">
                {products.map((product) => (
                  <ProductCard key={product.product_id} product={product} onAddToCart={handleAddToCart} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
